import React, { createContext, useState, useContext, useEffect } from "react";

const ZoneContext = createContext();

const LOCAL_STORAGE_KEY = "app_selected_zone";
const LOCAL_STORAGE_ZONES_KEY = "app_selected_zones";

const zoneOptions = [
    { value: "1", label: "Europa" },
    { value: "2", label: "México" },
    { value: "3", label: "Países no GOA" },
];

/* Zonas del filtro múltiple del dashboard. Si no hay nada válido guardado,
   arranca con todas las zonas seleccionadas. */
const getZonasIniciales = () => {
    try {
        const item = window.localStorage.getItem(LOCAL_STORAGE_ZONES_KEY);
        const guardadas = item ? JSON.parse(item) : null;

        if (!Array.isArray(guardadas)) return zoneOptions;

        const valores = guardadas.map((zona) => String(zona?.value ?? zona));

        const zonas = zoneOptions.filter((zona) =>
            valores.includes(zona.value)
        );

        return zonas.length > 0 ? zonas : zoneOptions;
    } catch (error) {
        console.error("Error al leer de localStorage", error);
        return zoneOptions;
    }
};

export const ZoneProvider = ({ children }) => {
    /* Zona única: la del desplegable del header, que usa el resto de la app. */
    const [selectedZone, setSelectedZone] = useState(() => {
        try {
            const item = window.localStorage.getItem(LOCAL_STORAGE_KEY);
            return item ? JSON.parse(item) : zoneOptions[1];
        } catch (error) {
            console.error("Error al leer de localStorage", error);
            return zoneOptions[1];
        }
    });

    /* Zonas múltiples: solo las píldoras del dashboard. Es independiente de
       la zona del header para no alterar el resto de pantallas. */
    const [selectedZones, setSelectedZones] = useState(getZonasIniciales);

    useEffect(() => {
        try {
            window.localStorage.setItem(
                LOCAL_STORAGE_KEY,
                JSON.stringify(selectedZone)
            );
        } catch (error) {
            console.error("Error al guardar en localStorage", error);
        }
    }, [selectedZone]);

    useEffect(() => {
        try {
            window.localStorage.setItem(
                LOCAL_STORAGE_ZONES_KEY,
                JSON.stringify(selectedZones)
            );
        } catch (error) {
            console.error("Error al guardar en localStorage", error);
        }
    }, [selectedZones]);

    const isZoneSelected = (value) =>
        selectedZones.some((zona) => zona.value === String(value));

    /* Alterna una zona manteniendo el orden de zoneOptions.
       Nunca se queda sin ninguna zona seleccionada. */
    const toggleZone = (value) => {
        const codZona = String(value);

        setSelectedZones((prev) => {
            const seleccionada = prev.some((zona) => zona.value === codZona);

            if (seleccionada) {
                if (prev.length === 1) return prev;
                return prev.filter((zona) => zona.value !== codZona);
            }

            return zoneOptions.filter(
                (zona) =>
                    zona.value === codZona ||
                    prev.some((z) => z.value === zona.value)
            );
        });
    };

    const selectZones = (valores) => {
        const codigos = (valores ?? []).map(String);

        const zonas = zoneOptions.filter((zona) =>
            codigos.includes(zona.value)
        );

        setSelectedZones(zonas.length > 0 ? zonas : zoneOptions);
    };

    const value = {
        selectedZone,
        setSelectedZone,
        selectedZones,
        selectedZoneValues: selectedZones.map((zona) => zona.value),
        toggleZone,
        selectZones,
        isZoneSelected,
        zoneOptions,
    };

    return (
        <ZoneContext.Provider value={value}>{children}</ZoneContext.Provider>
    );
};

export const useZone = () => {
    const context = useContext(ZoneContext);
    if (context === undefined) {
        throw new Error("useZone must be used within a ZoneProvider");
    }
    return context;
};
